import { useState, useRef, useEffect, useMemo } from 'react'
import { COUNTRIES, type Country } from '@/lib/countries'
import { ChevronDownIcon, SearchMdIcon, CheckIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

interface CountrySelectProps {
  value: string
  onChange: (code: string) => void
  label?: string
  className?: string
}

export function CountrySelect({ value, onChange, label, className }: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selected = COUNTRIES.find((c) => c.code === value) || COUNTRIES[0]

  const filtered = useMemo(() => {
    if (!search) return COUNTRIES
    const q = search.toLowerCase()
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q)
    )
  }, [search])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Focus search when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Scroll selected into view when opening
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedEl = listRef.current.querySelector('[data-selected="true"]')
      selectedEl?.scrollIntoView({ block: 'nearest' })
    }
  }, [isOpen])

  const handleSelect = (country: Country) => {
    onChange(country.code)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <div className={cn('flex flex-col gap-1', className)} ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-[#666]">{label}</label>
      )}
      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between rounded-[7px] border bg-white px-[15px] py-[9px] text-left text-sm text-[#181d27] outline-none transition-colors',
            isOpen ? 'border-[#181d27]' : 'border-[#e7eaeb]'
          )}
        >
          <span>
            {selected.flag} {selected.name}
          </span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 text-[#a4a7ae] transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-[rgba(0,0,0,0.08)] bg-white shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)]">
            {/* Search */}
            <div className="border-b border-[#e7eaeb] p-2">
              <div className="relative">
                <SearchMdIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a4a7ae]" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country..."
                  className="w-full rounded-md border border-[#e7eaeb] bg-white py-1.5 pl-8 pr-3 text-sm text-[#181d27] placeholder:text-[#a4a7ae] outline-none focus:border-[#181d27]"
                />
              </div>
            </div>

            {/* List */}
            <ul ref={listRef} className="max-h-[200px] overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-[#717680]">No countries found</li>
              ) : (
                filtered.map((country) => {
                  const isSelected = country.code === value
                  return (
                    <li key={country.code} data-selected={isSelected}>
                      <button
                        type="button"
                        onClick={() => handleSelect(country)}
                        className={cn(
                          'flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-sm hover:bg-[#f9fafb]',
                          isSelected && 'bg-[#f9fafb]'
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-base">{country.flag}</span>
                          <span className="text-[#181d27]">{country.name}</span>
                        </span>
                        {isSelected && (
                          <CheckIcon className="h-4 w-4 text-[#368f8b]" />
                        )}
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
